import { useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import type { PrintDesignRender } from '@wrapforce/shared';
import { Button } from '@/components/ui/button';
import { useLocaleFormatters } from '@/lib/format';
import { fetchPrintRenderBlob } from './api';
import { createZip } from './zip';

// Slide order within one render set: side, front, rear. A legacy set (built
// before straight front/rear renders existed) used three-quarter angles
// instead of front/rear - those still slot into the same front/rear
// positions so an old set's carousel keeps its original order, just with
// its original (three-quarter) caption and file suffix.
const ANGLE_ORDER_INDEX: Record<string, number> = {
  side: 0,
  front: 1,
  three_quarter_front: 1,
  rear: 2,
  three_quarter_rear: 2,
};
const ANGLE_LABEL_KEY: Record<string, string> = {
  side: 'generate.angleSide',
  front: 'generate.angleFront',
  rear: 'generate.angleRear',
  three_quarter_front: 'generate.angleThreeQuarterFront',
  three_quarter_rear: 'generate.angleThreeQuarterRear',
};
const SIDE_LABEL_KEY: Record<string, string> = {
  driver: 'generate.sideDriver',
  passenger: 'generate.sidePassenger',
};
// Fixed, non-localized zip entry suffixes (a print/production file name
// stays ASCII-stable across locales) - the design name itself is not
// translated either.
const ANGLE_FILE_SUFFIX: Record<string, string> = {
  side: 'side',
  front: 'front',
  rear: 'rear',
  three_quarter_front: 'front three-quarter',
  three_quarter_rear: 'rear three-quarter',
};

function sanitizeFileName(name: string): string {
  // eslint-disable-next-line no-useless-escape
  return name.replace(/[\/\\:*?"<>|]/g, '').trim();
}

function extensionForMime(mime: string): string {
  if (mime.includes('png')) return 'png';
  if (mime.includes('webp')) return 'webp';
  return 'jpg';
}

const SWIPE_THRESHOLD_PX = 40;

export function RenderSetCarousel({
  workspaceId, designId, designName, renders, onOpenRender,
}: {
  workspaceId: string;
  designId: string;
  designName: string;
  renders: PrintDesignRender[];
  onOpenRender: (url: string) => void;
}) {
  const { t } = useTranslation('printWraps');
  const { formatDateTime } = useLocaleFormatters();
  const [index, setIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  const activeSlides = [...renders].sort(
    (a, b) => (ANGLE_ORDER_INDEX[a.angle] ?? 99) - (ANGLE_ORDER_INDEX[b.angle] ?? 99),
  );
  const current = activeSlides[Math.min(index, activeSlides.length - 1)];
  const canGoPrev = index > 0;
  const canGoNext = index < activeSlides.length - 1;

  function goPrev() {
    setIndex((i) => Math.max(0, i - 1));
  }
  function goNext() {
    setIndex((i) => Math.min(activeSlides.length - 1, i + 1));
  }

  function handleKeyDown(e: ReactKeyboardEvent) {
    if (e.key === 'ArrowLeft') goPrev();
    else if (e.key === 'ArrowRight') goNext();
  }

  function handlePointerDown(e: ReactPointerEvent) {
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
  }
  function handlePointerUp(e: ReactPointerEvent) {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goNext();
    else goPrev();
  }

  const anySucceeded = renders.some((r) => r.status === 'succeeded' && r.url);
  const isSingle = activeSlides.length === 1;

  // A legacy render (or a single customer-photo-mode render) is one slide,
  // not a set - downloading it is just the one image, not a zip.
  async function handleDownloadSingle(render: PrintDesignRender) {
    if (render.status !== 'succeeded' || !render.url) return;
    setDownloading(true);
    try {
      const blob = await fetchPrintRenderBlob(workspaceId, designId, render.id);
      const suffix = ANGLE_FILE_SUFFIX[render.angle] ?? render.angle;
      const ext = extensionForMime(blob.type);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFileName(designName)} - ${suffix}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('generate.toast.downloadAllFailed'));
    } finally {
      setDownloading(false);
    }
  }

  async function handleDownloadAll() {
    setDownloading(true);
    try {
      const entries: { name: string; data: Uint8Array }[] = [];
      for (const render of activeSlides) {
        if (render.status !== 'succeeded' || !render.url) continue;
        const blob = await fetchPrintRenderBlob(workspaceId, designId, render.id);
        const suffix = ANGLE_FILE_SUFFIX[render.angle] ?? render.angle;
        const ext = extensionForMime(blob.type);
        entries.push({
          name: `${sanitizeFileName(designName)} - ${suffix}.${ext}`,
          data: new Uint8Array(await blob.arrayBuffer()),
        });
      }
      if (entries.length === 0) return;
      const zip = createZip(entries);
      const url = URL.createObjectURL(zip);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFileName(designName)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('generate.toast.downloadAllFailed'));
    } finally {
      setDownloading(false);
    }
  }

  function handleDownloadClick() {
    if (isSingle) void handleDownloadSingle(activeSlides[0]!);
    else void handleDownloadAll();
  }

  if (!current) return null;

  return (
    <div
      className="flex flex-col gap-1.5 border border-border p-2 outline-none"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className="relative aspect-[8/3] w-full bg-surface-2"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        {current.status === 'succeeded' && current.url ? (
          <button
            type="button"
            onClick={() => onOpenRender(current.url!)}
            aria-label={t('generate.viewRender')}
            className="block h-full w-full cursor-zoom-in"
          >
            <img src={current.url} alt="" className="h-full w-full object-contain" />
          </button>
        ) : current.status === 'pending' ? (
          <div className="grid h-full w-full place-items-center text-xs text-muted">{t('generate.renderPending')}</div>
        ) : (
          <div className="grid h-full w-full place-items-center px-2 text-center text-xs text-danger">{t('generate.renderFailedRefunded')}</div>
        )}
        {activeSlides.length > 1 ? (
          <>
            <button
              type="button"
              onClick={goPrev}
              disabled={!canGoPrev}
              aria-label={t('generate.carouselPrev')}
              className="absolute left-1 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext}
              aria-label={t('generate.carouselNext')}
              className="absolute right-1 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full bg-black/40 text-white disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </>
        ) : null}
      </div>
      {activeSlides.length > 1 ? (
        <div className="flex items-center justify-center gap-1">
          {activeSlides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={t('generate.carouselGoTo', { n: i + 1 })}
              className={`h-1.5 w-1.5 rounded-full ${i === index ? 'bg-ink' : 'bg-border'}`}
            />
          ))}
        </div>
      ) : null}
      <span className="text-[11px] text-muted">
        {t(ANGLE_LABEL_KEY[current.angle] ?? 'generate.angleSide')}
        {current.side ? ` · ${t(SIDE_LABEL_KEY[current.side] ?? 'generate.sideDriver')}` : ''}
        {' · '}{formatDateTime(new Date(current.createdAt))}
      </span>
      <Button size="sm" variant="outline" disabled={!anySucceeded || downloading} loading={downloading} onClick={handleDownloadClick}>
        <Download size={12} className="mr-1" /> {isSingle ? t('generate.download') : t('generate.downloadAll')}
      </Button>
    </div>
  );
}
